import type {
    default as Torus,
    LOGIN_TYPE,
    Maybe,
    RequestArguments,
    TorusInpageProvider,
    UserInfo,
} from "@toruslabs/torus-embed";
import { retryWithBackoff } from "@novel/shared/utils/retryWithBackoff";
import Web3 from "web3";
import CoinbaseWalletSDK, { CoinbaseWalletProvider } from "@coinbase/wallet-sdk";
import Logo from "@novel/shared/icons/logo.png";
import type { MetaMaskInpageProvider } from "@metamask/providers";
import { PublicKey, Transaction, SendOptions } from "@solana/web3.js";
import { SurfaceableError } from "@novel/shared/utils/SurfaceableError";
import WalletConnectProvider from "@walletconnect/web3-provider";
import { NETWORK_MAP } from "@novel/shared/utils/wallets/networkMap";
import { getFromLocalStorage, unsetInLocalStorage } from "@novel/shared/utils/localStorageUtils";
import {
    checkDevice,
    deepLinkRedirect,
    redirectOnFrontend,
} from "@novel/shared/utils/locationUtils";
import UniversalProvider from "@walletconnect/universal-provider";
import Client from "@walletconnect/sign-client";
import detectEthereumProvider from "@metamask/detect-provider";
import { PairingTypes, SessionTypes } from "@walletconnect/types";
import { appPrefixedHostWithProtocol } from "@novel/shared/utils/appDomainConstants";
import QRCodeModal from "@walletconnect/qrcode-modal";

/*
    torusClient (this file)  is JUST responsible for fetching the 
    mainnet and testnet clients from cache (if present) or instantiating
    them if they're not in the cache
*/

const WALLET_CONNECT_PROJECT_ID = process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID || "";
interface WalletClientMap {
    torus: TorusWalletClient;
    metamask: MetamaskWalletClient;
    "wallet-connect": WalletConnectClient;
    "wallet-connect-v2": WalletConnectV2Client;
    coinbase: CoinbaseWalletClient;
    phantom: SolanaWalletClient;
}

export type WalletProvider = WalletClientMap[keyof WalletClientMap]["provider"];
export type EvmWalletProvider = Omit<WalletClientMap, "phantom">[keyof Omit<
    WalletClientMap,
    "phantom"
>]["provider"];

export async function getWalletClient<T extends keyof WalletClientMap>(
    walletProviderType: T,
): Promise<WalletClientMap[T]> {
    // uncertain why this ReturnType casting is needed for TS to be happy but it is
    type ReturnType = WalletClientMap[T];
    if (walletProviderType === "torus") {
        return (await getTorusInstance()) as ReturnType;
    } else if (walletProviderType === "metamask") {
        return (await getMetamaskInstance()) as ReturnType;
    } else if (walletProviderType === "wallet-connect") {
        return (await getWalletConnectInstance()) as ReturnType;
    } else if (walletProviderType === "wallet-connect-v2") {
        return (await getWalletConnectV2Instance()) as ReturnType;
    } else if (walletProviderType === "coinbase") {
        return (await getCoinbaseInstance()) as ReturnType;
    } else {
        return (await getPhantomInstance()) as ReturnType;
    }
}

export function isWalletExtensionInstalled(
    walletProviderType: keyof WalletClientMap,
): boolean | undefined {
    switch (walletProviderType) {
        case "metamask": {
            return (
                !!providerIsMetamask((window as any).ethereum) ||
                !!(window as any).ethereum?.providers?.some((provider: any) =>
                    providerIsMetamask(provider),
                )
            );
        }
        case "coinbase": {
            return (
                !!(window as any).ethereum?.isCoinbaseWallet ||
                !!(window as any).ethereum?.providers?.some(
                    (provider: any) => provider.isCoinbaseWallet,
                )
            );
        }
        case "phantom": {
            let status = false;
            scopePollingDetectionStrategy(() => {
                if (
                    (window as PhantomWindow).phantom?.solana?.isPhantom ||
                    (window as PhantomWindow).solana?.isPhantom
                ) {
                    status = true;
                    return true;
                }
                status = false;
                return false;
            });
            return status;
        }
        default: {
            return undefined;
        }
    }
}

// taken from rainbow kit's "isMetaMask" function
// https://github.com/rainbow-me/rainbowkit/blob/main/packages/rainbowkit/src/wallets/walletConnectors/metaMask/metaMask.ts
function providerIsMetamask(provider: MetaMaskInpageProvider) {
    // Logic borrowed from wagmi's MetaMaskConnector
    // https://github.com/tmm/wagmi/blob/main/packages/core/src/connectors/metaMask.ts
    const isMetaMask = Boolean(provider?.isMetaMask);
    if (!isMetaMask) {
        return false;
    }

    // Brave tries to make itself look like MetaMask
    // Could also try RPC `web3_clientVersion` if following is unreliable
    if (
        (provider as any).isBraveWallet &&
        !(provider as any)._events &&
        !(provider as any)._state
    ) {
        return false;
    }
    if ((provider as any).isTokenPocket) return false;
    if ((provider as any).isTokenary) return false;
    if ((provider as any).isAvalanche) return false;
    if ((provider as any).isPortal) return false;

    return true;
}

export interface EvmWalletClient {
    provider: {
        request<T>(args: RequestArguments): Promise<Maybe<T>>;
    };
    web3Instance: Web3;
}

interface TorusWalletClient extends EvmWalletClient {
    client: Torus;
    provider: TorusInpageProvider;
}

let initializedTorusProvider: Promise<TorusWalletClient>;
export function getTorusInstance(): Promise<TorusWalletClient> {
    if (!initializedTorusProvider) {
        initializedTorusProvider = _getTorusInstance();
    }
    return initializedTorusProvider;
}
async function _getTorusInstance(): Promise<TorusWalletClient> {
    if (typeof window === "undefined") {
        return initializedTorusProvider;
    }

    const TorusConstructor = await import("@toruslabs/torus-embed");

    const torusConfig = {
        whiteLabel: {
            disclaimerHide: true,
            featuredBillboardHide: true,
            theme: {
                isDark: false,
                colors: {
                    torusBrand1: "#3193dc",
                    torusBrand2: "#000",
                },
            },
            defaultLanguage: "en",
            logoDark: "https://tkey.surge.sh/images/Device.svg", // Dark logo for light background
            logoLight: "https://tkey.surge.sh/images/Device.svg", // Light logo for dark background
        },
        skipTKey: true, // skips some extra Torus UI steps
        showTorusButton: false,
        useLocalStorage: true, // torus optimization with this is way too buggy, need to disable
        loginConfig: {
            "torus-auth0-email-passwordless": {
                typeOfLogin: "passwordless" as LOGIN_TYPE,
                name: "Passwordless",
                showOnModal: false,
            },
        },
    };

    const torusClient = new TorusConstructor.default();

    await Promise.all([
        retryWithBackoff(() =>
            torusClient.init({
                ...torusConfig,
                network: {
                    host: "matic", // polygon
                    networkName: "Polygon",
                    chainId: NETWORK_MAP.MATIC.CHAIN_ID_DECIMAL,
                },
            }),
        ),
    ]);

    const torusWeb3Provider = new Web3(torusClient.provider as any);

    return {
        client: torusClient,
        provider: torusClient.provider,
        web3Instance: torusWeb3Provider,
    };
}

const torusUserInfoCache = new Map<string, Promise<UserInfo>>();
export function getTorusUserInfo(torusClient: Torus): Promise<UserInfo> {
    if (!torusUserInfoCache.has(torusClient.currentVerifier)) {
        torusUserInfoCache.set(
            torusClient.currentVerifier,
            retryWithBackoff(() =>
                (torusClient as any).getUserInfo().catch((e: any) => {
                    torusUserInfoCache.delete(torusClient.currentVerifier);
                    throw new Error(e);
                }),
            ),
        );
    }

    return torusUserInfoCache.get(torusClient.currentVerifier)!;
}

const APP_NAME = "Novel";
const APP_LOGO_URL = (Logo as any).src;

interface CoinbaseWalletClient extends EvmWalletClient {
    provider: CoinbaseWalletProvider;
}

let initializedCoinbaseClient: CoinbaseWalletClient;
export function getCoinbaseInstance(): CoinbaseWalletClient {
    if (!initializedCoinbaseClient) {
        const initializedCoinbaseWalletSdk = new CoinbaseWalletSDK({
            appName: APP_NAME,
            appLogoUrl: APP_LOGO_URL,
            darkMode: false,
        });
        const provider = initializedCoinbaseWalletSdk.makeWeb3Provider(
            NETWORK_MAP.MATIC.RPC_URL,
            NETWORK_MAP.MATIC.CHAIN_ID_DECIMAL,
        );
        initializedCoinbaseClient = {
            provider,
            web3Instance: new Web3(provider as any),
        };
    }

    return initializedCoinbaseClient;
}

export const redirectToCoinbaseApp = (redirectUrl?: string): void => {
    if (checkDevice() === "iOS") {
        // coinbase deeplinking on iOS is super finnicky, must use "window.location.href =" instead of "window.open"
        // also adding a setTimeout make the deeplink prompt appear which makes the deep linking actually work
        setTimeout(() => {
            deepLinkRedirect(
                `cbwallet://dapp?url=${encodeURIComponent(redirectUrl || window.location.href)}`,
            );
        }, 1000);
    } else {
        redirectOnFrontend(
            `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(
                redirectUrl || window.location.href,
            )}`,
        );
    }
};

interface MetamaskWalletClient extends EvmWalletClient {
    provider: MetaMaskInpageProvider;
}

let initializedMetamaskClient: Promise<MetamaskWalletClient>;
export async function getMetamaskInstance(): Promise<MetamaskWalletClient> {
    if (!initializedMetamaskClient) {
        initializedMetamaskClient = _getMetamaskInstance();
    }

    return initializedMetamaskClient;
}

export const detectMetaMaskProvider = async (): Promise<boolean> => {
    const METAMASK_INITIALIZATION_TIMEOUT = 20000;
    /// This is not the actual provider it's just for check
    const detectedProvider = (await detectEthereumProvider({
        mustBeMetaMask: true,
        timeout: METAMASK_INITIALIZATION_TIMEOUT,
    })) as MetaMaskInpageProvider | undefined;
    if (!detectedProvider) {
        return false;
    }
    return true;
};

async function _getMetamaskInstance(): Promise<MetamaskWalletClient> {
    const { ethereum } = window as any;

    const detectedProvider = await detectMetaMaskProvider();
    if (!detectedProvider) {
        throw new SurfaceableError("You do not have Metamask installed!");
    }

    if (ethereum?.providers && ethereum?.providers.length > 1) {
        const provider: MetaMaskInpageProvider = ethereum.providers.find((provider: any) =>
            providerIsMetamask(provider),
        );
        await ethereum.setSelectedProvider(provider);
    } else {
        // this throws if the user does not have multiple extensions installed
        try {
            ethereum.selectedProvider = window.ethereum;
            // eslint-disable-next-line no-empty
        } catch (e: any) {}
    }

    if (!providerIsMetamask((window as any)?.ethereum)) {
        throw new SurfaceableError("You do not have Metamask installed");
    }

    return {
        provider: ethereum.selectedProvider,
        web3Instance: new Web3(ethereum.selectedProvider as any),
    };
}

export const redirectToMetaMaskApp = (redirectUrl?: string): void => {
    const slicedRedirectUrl = redirectUrl ? redirectUrl : window.location.href;
    deepLinkRedirect(`https://metamask.app.link/dapp/${slicedRedirectUrl}`);
};

interface WalletConnectClient extends EvmWalletClient {
    provider: WalletConnectProvider;
}

let initializedWalletConnectClient: WalletConnectClient;
export async function getWalletConnectInstance(): Promise<WalletConnectClient> {
    if (!initializedWalletConnectClient) {
        // polyfill is needed for wallet connect v1
        await import("buffer").then(({ Buffer }) => {
            if (typeof window !== "undefined") {
                (window as any).Buffer = Buffer;
            }
        });

        const WalletConnectProvider = await import("@walletconnect/web3-provider").then(
            ({ default: WalletConnectProvider }) => WalletConnectProvider,
        );
        const providerPolygon = new WalletConnectProvider({
            rpc: {
                [NETWORK_MAP.MATIC.CHAIN_ID_DECIMAL]: NETWORK_MAP.MATIC.RPC_URL,
            },
            chainId: NETWORK_MAP.MATIC.CHAIN_ID_DECIMAL,
        });
        const web3InstancePolygon = new Web3(providerPolygon as any);
        initializedWalletConnectClient = {
            provider: web3InstancePolygon.currentProvider as unknown as WalletConnectProvider,
            web3Instance: web3InstancePolygon,
        };
    }
    return initializedWalletConnectClient;
}

// the wallet connect check is insufficient because it does not count localstorage cache
export function isWalletConnectConnected(provider: WalletConnectProvider): boolean {
    return provider.isWalletConnect || !!getFromLocalStorage("walletconnect");
}

// wallet connect fails to reset this state if
// the user closes the popup this takes care of that
export function clearWalletConnectIsConnecting(provider: WalletConnectProvider): void {
    provider.isConnecting = false;
    if ((provider?.wc as any)?._handshakeId) {
        (provider.wc as any)._handshakeId = 0;
    }
    if ((provider?.wc as any)?._handshakeTopic) {
        (provider.wc as any)._handshakeTopic = "";
    }
}

// similarly wallet connect fails to reset this state if
// for disconnecting, it causes the popup to close immediately on reopen
export async function clearWalletConnected(provider: WalletConnectProvider): Promise<void> {
    // await (provider.wc as any)._sendRequest(
    //     (provider.wc as any)._formatRequest({
    //         method: "wc_sessionUpdate",
    //         params: [
    //             {
    //                 approved: false,
    //                 chainId: null,
    //                 networkId: null,
    //                 accounts: null,
    //             },
    //         ],
    //     }),
    // );
    if ((provider?.wc as any)?._accounts?.length) {
        (provider.wc as any)._accounts = [];
    }
    if ((provider?.wc as any)?._connected) {
        (provider.wc as any)._connected = false;
    }
    if ((provider?.wc as any)?._peerId) {
        (provider.wc as any)._peerId = "";
    }
    if ((provider?.wc as any)?._peerId) {
        (provider.wc as any)._peerId = "";
    }
    (provider.wc as any)._removeStorageSession();
    (provider.wc as any).transportClose();

    unsetInLocalStorage("-walletlink:https://www.walletlink.org:session:id");
    unsetInLocalStorage("-walletlink:https://www.walletlink.org:session:secret");
    unsetInLocalStorage("-walletlink:https://www.walletlink.org:session:linked");
    unsetInLocalStorage("-walletlink:https://www.walletlink.org:version");
    unsetInLocalStorage("walletconnect");

    clearWalletConnectIsConnecting(provider);
}

type PhantomEvent = "disconnect" | "connect" | "accountChanged";
interface ConnectOpts {
    onlyIfTrusted: boolean;
}

export interface PhantomWindow extends Window {
    phantom?: {
        solana?: SolanaProvider;
    };
    solana?: SolanaProvider;
}

type DisplayEncoding = "utf8" | "hex";

type PhantomRequestMethod =
    | "connect"
    | "disconnect"
    | "signAndSendTransaction"
    | "signTransaction"
    | "signAllTransactions"
    | "signMessage";

interface ConnectOpts {
    onlyIfTrusted: boolean;
}

export interface SolanaProvider {
    publicKey: PublicKey | null;
    isConnected: boolean | null;
    signAndSendTransaction: (
        transaction: Transaction,
        opts?: SendOptions,
    ) => Promise<{ signature: string; publicKey: PublicKey }>;
    signTransaction: (transaction: Transaction) => Promise<Transaction>;
    signAllTransactions: (transactions: Transaction[]) => Promise<Transaction[]>;
    signMessage: (message: Uint8Array | string, display?: DisplayEncoding) => Promise<any>;
    connect: (opts?: Partial<ConnectOpts>) => Promise<{ publicKey: PublicKey }>;
    disconnect: () => Promise<void>;
    on: (event: PhantomEvent, handler: (args: any) => void) => void;
    request: (method: PhantomRequestMethod, params: any) => Promise<unknown>;
    isPhantom: boolean;
}

export type Status = "success" | "warning" | "error" | "info";

export interface TLog {
    status: Status;
    method?: PhantomRequestMethod | Extract<PhantomEvent, "accountChanged">;
    message: string;
    messageTwo?: string;
}

export interface SolanaWalletClient {
    provider: SolanaProvider;
}

export function getPhantomInstance(): SolanaWalletClient {
    const getProvider = () => {
        if ("phantom" in window) {
            const provider =
                (window as PhantomWindow).phantom?.solana ||
                ((window as PhantomWindow).solana! as SolanaProvider);
            if (provider?.isPhantom) {
                return provider;
            }
        }
    };

    const provider = getProvider();
    if (!provider) {
        throw new SurfaceableError("You do not have the Phantom browser extension enabled!");
    }

    return {
        provider,
    };
}

export const redirectToPhantomApp = (redirectUrl?: string): void => {
    deepLinkRedirect(
        `https://phantom.app/ul/browse/${encodeURIComponent(redirectUrl || window.location.href)}`,
    );
};

export function scopePollingDetectionStrategy(detect: () => boolean): void {
    // Early return when server-side rendering
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const disposers: (() => void)[] = [];

    function detectAndDispose() {
        const detected = detect();
        if (detected) {
            for (const dispose of disposers) {
                dispose();
            }
        }
    }

    // Strategy #1: Try detecting every second.
    const interval =
        // TODO: #334 Replace with idle callback strategy.
        setInterval(detectAndDispose, 1000);
    disposers.push(() => clearInterval(interval));

    // Strategy #2: Detect as soon as the DOM becomes 'ready'/'interactive'.
    if (
        // Implies that `DOMContentLoaded` has not yet fired.
        document.readyState === "loading"
    ) {
        document.addEventListener("DOMContentLoaded", detectAndDispose, { once: true });
        disposers.push(() => document.removeEventListener("DOMContentLoaded", detectAndDispose));
    }

    // Strategy #3: Detect after the `window` has fully loaded.
    if (
        // If the `complete` state has been reached, we're too late.
        document.readyState !== "complete"
    ) {
        window.addEventListener("load", detectAndDispose, { once: true });
        disposers.push(() => window.removeEventListener("load", detectAndDispose));
    }

    // Strategy #4: Detect synchronously, now.
    detectAndDispose();
}

interface WalletConnectV2Client extends EvmWalletClient {
    provider: UniversalProvider;
    client?: Client;
    session?: SessionTypes.Struct;
    pairings?: PairingTypes.Struct[];
    accounts?: string[];
    hasCheckedPersistedSession?: boolean;
}

let initializedWalletConnectV2Client: WalletConnectV2Client;
const walletConnectV2Reset = () => {
    initializedWalletConnectV2Client.pairings = [];
    initializedWalletConnectV2Client.session = undefined as any;
    initializedWalletConnectV2Client.accounts = [];
};

export const disconnectWalletV2Connect = async () => {
    if (typeof initializedWalletConnectV2Client.provider === "undefined") {
        throw new Error("Provider is not initialized");
    }
    await initializedWalletConnectV2Client.provider.disconnect();
    walletConnectV2Reset();
};

const _subscribeToProviderEvents = async (_client: UniversalProvider) => {
    if (typeof _client === "undefined") {
        throw new Error("WalletConnect is not initialized");
    }

    _client.on("display_uri", async (uri: string) => {
        console.log("EVENT", "QR Code Modal open");
        console.log("uri ", uri);
        QRCodeModal.open(uri, () => {
            console.log("EVENT", "QR Code Modal closed");
        });
    });

    // Subscribe to session ping
    _client.on("session_ping", ({ id, topic }: { id: number; topic: string }) => {
        console.log("EVENT", "session_ping");
        console.log(id, topic);
    });

    // Subscribe to session connect
    _client.on("session_connect", ({ session }: { session: SessionTypes.Struct }) => {
        console.log("EVENT", "session_connect");
        console.log(session);
    });

    // Subscribe to session event
    _client.on("session_event", ({ event, chainId }: { event: any; chainId: string }) => {
        console.log("EVENT", "session_event");
        console.log(event, chainId);
    });

    // Subscribe to session update
    _client.on(
        "session_update",
        ({ topic, session }: { topic: string; session: SessionTypes.Struct }) => {
            console.log("EVENT", "session_updated");
            initializedWalletConnectV2Client.session = session;
        },
    );

    // Subscribe to session delete
    _client.on("session_delete", ({ id, topic }: { id: number; topic: string }) => {
        console.log("EVENT", "session_deleted");
        console.log(id, topic);
        disconnectWalletV2Connect();
    });
};

export const walletConnectConnection = async (pairingTopic: string | undefined) => {
    if (!initializedWalletConnectV2Client.provider) {
        throw new ReferenceError("WalletConnect Client is not initialized.");
    }
    try {
        const session = await initializedWalletConnectV2Client.provider.connect({
            namespaces: {
                eip155: {
                    methods: [
                        "eth_sendTransaction",
                        "eth_signTransaction",
                        "eth_sign",
                        "personal_sign",
                        "eth_signTypedData",
                        "eth_sendRawTransaction",
                    ],
                    chains: [`eip155:${[NETWORK_MAP.MATIC.CHAIN_ID_DECIMAL]}`],
                    events: ["chainChanged", "accountsChanged", "connect", "disconnect"],
                    rpcMap: {
                        [NETWORK_MAP.MATIC.CHAIN_ID_DECIMAL]: NETWORK_MAP.MATIC.RPC_URL,
                    },
                },
            },
            pairingTopic,
        });

        createWeb3Provider(initializedWalletConnectV2Client.provider);
        initializedWalletConnectV2Client.session = session;
        const _accounts = await initializedWalletConnectV2Client.provider.enable();
        initializedWalletConnectV2Client.accounts = _accounts;
    } catch (err) {
        console.log("err ", err);
    } finally {
        QRCodeModal.close();
    }
};

export const _checkForPersistedSession = async (provider: UniversalProvider) => {
    if (typeof provider === "undefined") {
        throw new Error("WalletConnect is not initialized");
    }
    const pairings = provider.client.pairing.getAll({ active: true });
    // populates existing pairings to state
    initializedWalletConnectV2Client.pairings = pairings;
    console.log("RESTORED PAIRINGS: ", pairings);
    if (typeof initializedWalletConnectV2Client.session !== "undefined") return;
    // populates (the last) existing session to state
    if (initializedWalletConnectV2Client.provider?.session) {
        const _session = initializedWalletConnectV2Client.provider?.session;
        console.log("RESTORED SESSION:", _session);
        await onSessionConnected(_session);
        return _session;
    }
};

export const onSessionConnected = async (_session: SessionTypes.Struct) => {
    if (!initializedWalletConnectV2Client.provider) {
        throw new ReferenceError("EthereumProvider is not initialized.");
    }
    const allNamespaceAccounts = Object.values(_session.namespaces)
        .map((namespace) => namespace.accounts)
        .flat();
    initializedWalletConnectV2Client.session = _session;
    initializedWalletConnectV2Client.accounts = allNamespaceAccounts.map(
        (account) => account.split(":")[2],
    );
    console.log("RESTORED", allNamespaceAccounts);
};

export const getPersistedSession = async () => {
    if (!initializedWalletConnectV2Client.provider) return;
    await _checkForPersistedSession(initializedWalletConnectV2Client.provider);
    initializedWalletConnectV2Client.hasCheckedPersistedSession = true;
};

export async function getWalletConnectV2Instance(): Promise<WalletConnectV2Client> {
    if (!initializedWalletConnectV2Client) {
        const provider = await UniversalProvider.init({
            projectId: WALLET_CONNECT_PROJECT_ID,
            // logger: "debug",
            relayUrl: "wss://relay.walletconnect.com",
            metadata: {
                name: "React App",
                description: "React App for WalletConnect",
                url: `${appPrefixedHostWithProtocol}`,
                icons: ["https://avatars.githubusercontent.com/u/37784886"],
            },
        });
        const web3Instance = new Web3(provider);
        initializedWalletConnectV2Client = {
            provider,
            web3Instance,
            client: provider.client,
        };
        QRCodeModal.close();
        await _subscribeToProviderEvents(provider);
    }
    if (
        initializedWalletConnectV2Client.provider &&
        !initializedWalletConnectV2Client.hasCheckedPersistedSession
    ) {
        await getPersistedSession();
    }
    return initializedWalletConnectV2Client;
}

const createWeb3Provider = (ethereumProvider: UniversalProvider) => {
    initializedWalletConnectV2Client.web3Instance = new Web3(ethereumProvider);
};

export function isWalletConnectV2Connected(): boolean {
    return (
        !!initializedWalletConnectV2Client.session &&
        !!initializedWalletConnectV2Client.accounts &&
        !!initializedWalletConnectV2Client.pairings
    );
}
